import type { Cell, Workbook } from 'exceljs';
import type { FormConfig, PageList, PageQuestion } from '../../config/types';
import {
  getListPageExcelSchema,
  validateListPageEditorRows,
} from '../list-page-excel/editor-model';
import { mapPsfaCdrClipboard } from '../list-page-excel/psfa-cdr-clipboard';
import { cdrAnchors, cdrFields, cdrTables } from './layout';

export type CdrSection = {
  id: string;
  title: string;
  group: string;
  page: PageList | PageQuestion;
  declaration?: PageQuestion;
  rows: string[][];
  values: Record<string, unknown>;
  notes: string[];
  sourceErrors?: string[];
};

const normalise = (text: string) => text.trim().replace(/\s+/g, ' ').toLowerCase();
const readCell = (cell: Cell): string => {
  const value = cell.value;
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    if ('richText' in value) return value.richText.map(({ text }) => text).join('');
    if ('hyperlink' in value) return value.text;
    throw new Error(
      `${cell.worksheet.name}!${cell.address}: replace formulas, dates or Excel errors with values before importing.`,
    );
  }
  return String(value);
};

export const parseCdrWorkbook = (
  workbook: Workbook,
  config: FormConfig,
): CdrSection[] | undefined => {
  const sheet = workbook.getWorksheet('Proforma');
  const organisation = workbook.getWorksheet('Organisation details');
  if (!sheet && !organisation) return undefined;
  if (!sheet || !organisation || config.id !== 'psfa')
    throw new Error('Use a 2025-26 or 2026-27 CDR workbook with its original worksheets.');
  const templateYear = /\b26\/27 CDR Guidance\b/i.test(sheet.getCell('D6').text)
    ? '2026-27'
    : '2025-26';
  for (const [address, heading] of Object.entries(cdrAnchors)) {
    if (normalise(sheet.getCell(address).text) !== normalise(heading)) {
      throw new Error(
        `The CDR layout has changed at Proforma!${address}. Use the original 2025-26 or 2026-27 CDR template.`,
      );
    }
  }
  for (const [address, heading] of Object.entries({
    B2: 'Department:',
    B3: 'Name of public body:',
    B5: 'Reporting quarter',
    B8: 'Name',
    B9: 'Email address',
    B12: 'Name',
    B13: 'Email address',
    B16: 'Name',
    B17: 'Email address',
  })) {
    if (normalise(organisation.getCell(address).text) !== normalise(heading))
      throw new Error(`The organisation details layout has changed at ${address}.`);
  }
  const sections: CdrSection[] = [];
  for (const group of config.sections)
    for (const subsection of group.subsections) {
      for (const page of subsection.pages) {
        if (page.pageType === 'information' || (!cdrTables[page.name] && !cdrFields[page.name]))
          continue;
        const result: CdrSection = {
          id: subsection.slug,
          title: subsection.title,
          group: group.title,
          page,
          rows: [],
          values: {},
          notes: [],
        };
        result.declaration = subsection.pages.find(
          (candidate): candidate is PageQuestion =>
            candidate.pageType === 'question' &&
            candidate.questions?.length === 1 &&
            candidate.questions[0].name.endsWith('_declare'),
        );
        const table = cdrTables[page.name];
        if (table) {
          const [header, last, count] = table;
          const rows = Array.from({ length: last - header + 1 }, (_, i) =>
            Array.from({ length: count }, (_, j) => readCell(sheet.getCell(header + i, 5 + j * 2))),
          );
          const mapped = mapPsfaCdrClipboard(
            rows,
            page.name,
            getListPageExcelSchema(page.questions || []).columns,
          );
          if (mapped.error) throw new Error(`${subsection.title}: ${mapped.error}`);
          result.rows = mapped.rows.filter((row) => row.some((value) => value.trim() !== ''));
          if (mapped.message) result.notes.push(mapped.message);
        } else {
          const source = page.name === 'department-select' ? organisation : sheet;
          result.values = Object.fromEntries(
            Object.entries(cdrFields[page.name]).map(([field, address]) => [
              field,
              readCell(source.getCell(address)),
            ]),
          );
          if (page.name === 'detected-bribery') {
            const entries = [112, 113]
              .map((row) =>
                ['E', 'G', 'I', 'K'].map((column) => readCell(sheet.getCell(`${column}${row}`))),
              )
              .filter((row) => row.some((value) => value.trim()));
            if (entries.length > 0) {
              const validation = validateListPageEditorRows(
                {
                  title: page.title,
                  pageName: page.name,
                  questions: page.questions || [],
                  answers: {},
                  tenant: 'psfa',
                },
                entries,
              );
              result.sourceErrors = validation.errors.map(
                (error) =>
                  `Workbook bribery row ${error.worksheetRow}: ${error.fieldTitle}: ${error.message}`,
              );
              for (const [field, index] of [
                ['detected_bribery_instances', 1],
                ['detected_bribery', 2],
              ] as const) {
                const values = entries.map((row) => row[index].replace(/[£,\s]/g, ''));
                result.values[field] = values.every((value) => /^\d+(\.\d+)?$/.test(value))
                  ? String(Number(values.reduce((sum, value) => sum + Number(value), 0).toFixed(2)))
                  : values.filter(Boolean).join(' + ');
              }
              const covid = [...new Set(entries.map((row) => normalise(row[0])))];
              result.values.detected_bribery_c19 = covid.length === 1 ? entries[0][0] : '';
              result.values.detected_bribery_comments = entries
                .map((row) =>
                  covid.length > 1 || validation.errors.length > 0
                    ? `COVID-19: ${row[0] || 'not supplied'}; instances: ${row[1]}; value: ${row[2]}. ${row[3]}`
                    : row[3],
                )
                .filter(Boolean)
                .join('\n');
              if (entries.length > 1)
                result.notes.push(
                  'The workbook bribery rows are combined into the form’s single response. Different COVID-19 answers are preserved in Comments and need review.',
                );
            }
          }
          if (page.name === 'department-select') {
            const period = String(result.values['report-period']);
            const quarter =
              period.match(/(?:quarter|q)\s*([1-4])/i) || period.match(/^\s*([1-4])\s*$/);
            const year = period.match(/\b(20\d{2})[-/](?:20)?(\d{2})\b/);
            result.values['report-period'] = quarter
              ? {
                  quarter: `Quarter ${quarter[1]} (${['April-June', 'July-September', 'October-December', 'January-March'][Number(quarter[1]) - 1]})`,
                  year: year ? `${year[1]}-${year[2]}` : templateYear,
                }
              : period;
            if (readCell(organisation.getCell('D4')).trim())
              result.notes.push(
                `Directorate / business unit has no matching form field: ${readCell(organisation.getCell('D4'))}`,
              );
            if (readCell(sheet.getCell('K5')).trim())
              result.notes.push(
                `Overall comments have no matching form field: ${readCell(sheet.getCell('K5'))}`,
              );
          }
        }
        sections.push(result);
      }
    }
  return sections;
};

export const readCdrWorkbook = async (file: File, config: FormConfig) => {
  if (!file.name.toLowerCase().endsWith('.xlsx')) throw new Error('Select an XLSX workbook.');
  if (file.size > 10 * 1024 * 1024) throw new Error('Select a workbook smaller than 10 MB.');
  const ExcelJS = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());
  return parseCdrWorkbook(workbook, config);
};
